import React, { useState, useEffect, useRef } from "react";
import {
  Button,
  TextField,
  Divider,
  Box,
  Typography,
  MenuItem,
  Chip,
  Checkbox,
  ListItemText,
} from "@mui/material";
import MicIcon from "@mui/icons-material/Mic";
import MicOffIcon from "@mui/icons-material/MicOff";

// Observation text is dynamically fetched from Django Admin models (choice.report_text configured on CounselingQuestionChoices)
const getAutoGeneratedText = (questionTitle, choice) => {
  if (!choice) return "";
  if (choice.report_text && choice.report_text.trim()) {
    return choice.report_text.trim();
  }
  return choice.title || "";
};

const DynamicCategorySection = ({
  category,
  questions = [],
  sessionId,
  answers = [],
  onAnswerChange,
  onSave,
  CustomComponent,
  customComponentProps = {},
  aiText = "",
}) => {
  const [isListening, setIsListening] = useState({});
  const [activeQuestionId, setActiveQuestionId] = useState(null);
  const micRef = useRef(null);

  console.log(category, "missing info check");

  useEffect(() => {
    // Initialize speech recognition
    const SpeechRecognition =
      window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      micRef.current = new SpeechRecognition();
      micRef.current.continuous = true;
      micRef.current.interimResults = true;
      micRef.current.lang = "en-US";
    }

    return () => {
      if (micRef.current) {
        micRef.current.stop();
        micRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (!micRef.current) return;

    const handleMicStart = () => {
      console.log("Mic started for question:", activeQuestionId);
    };

    const handleMicEnd = () => {
      console.log("Mic ended for question:", activeQuestionId);
      // Restart if still listening
      if (activeQuestionId && isListening[activeQuestionId]) {
        setTimeout(() => {
          if (micRef.current && isListening[activeQuestionId]) {
            micRef.current.start();
          }
        }, 100);
      }
    };

    const handleMicResult = (event) => {
      let finalTranscript = "";
      let interimTranscript = "";

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      if (finalTranscript) {
        console.log("Final transcript:", finalTranscript);
        // Update the specific question that has the active mic
        if (activeQuestionId) {
          onAnswerChange(sessionId, activeQuestionId, null, finalTranscript);
        }
      }
    };

    const handleMicError = (event) => {
      console.log("Mic error:", event.error);
      if (event.error === "no-speech") {
        // Restart mic if no speech detected
        if (activeQuestionId && isListening[activeQuestionId]) {
          setTimeout(() => {
            if (micRef.current && isListening[activeQuestionId]) {
              micRef.current.start();
            }
          }, 100);
        }
      }
    };

    micRef.current.onstart = handleMicStart;
    micRef.current.onend = handleMicEnd;
    micRef.current.onresult = handleMicResult;
    micRef.current.onerror = handleMicError;

    return () => {
      if (micRef.current) {
        micRef.current.onstart = null;
        micRef.current.onend = null;
        micRef.current.onresult = null;
        micRef.current.onerror = null;
      }
    };
  }, [isListening, activeQuestionId, sessionId, onAnswerChange]);

  const toggleListening = (questionId) => {
    if (!micRef.current) {
      console.log("Speech recognition not supported");
      return;
    }

    if (isListening[questionId]) {
      // Stop listening for this question
      console.log("Stopping mic for question:", questionId);
      setIsListening((prev) => ({ ...prev, [questionId]: false }));
      setActiveQuestionId(null);
      micRef.current.stop();
    } else {
      // Stop any other active mic first
      if (activeQuestionId && isListening[activeQuestionId]) {
        console.log("Stopping previous mic for question:", activeQuestionId);
        setIsListening((prev) => ({ ...prev, [activeQuestionId]: false }));
        micRef.current.stop();
      }

      // Start listening for this question
      console.log("Starting mic for question:", questionId);
      setIsListening((prev) => ({ ...prev, [questionId]: true }));
      setActiveQuestionId(questionId);

      // Small delay to ensure previous mic is stopped
      setTimeout(() => {
        if (micRef.current) {
          micRef.current.start();
        }
      }, 200);
    }
  };

  // Question types: 1 = MCQ (one answer), 2 = typed, 3 = MSQ (several answers).
  // Option questions first, typed ones after.
  const isTyped = (q) => q.question_type === 2;
  const sortedQuestions = [...questions].sort((a, b) => isTyped(a) - isTyped(b));

  return (
    <div style={{ margin: "24px 0" }}>
      <Typography
        variant="h5"
        sx={{
          color: "#1e293b",
          fontWeight: 700,
          mb: 2.5,
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}
      >
        {category.title}
      </Typography>

      {sortedQuestions.map((question) => {
        const existing = answers.find((a) => a.question === question.id);
        const selectedChoiceId = existing?.choice || "";
        const selectedChoice = question.choices?.find(
          (c) => c.id === selectedChoiceId
        );
        const autoText = selectedChoice
          ? getAutoGeneratedText(question.title, selectedChoice)
          : "";

        return (
          <Box
            key={question.id}
            sx={{
              background: "#fff",
              border: "1px solid #e2e8f0",
              borderRadius: "12px",
              p: 2.5,
              mb: 2.5,
              boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
            }}
          >
            <Typography
              sx={{
                fontSize: "15px",
                fontWeight: 600,
                color: "#1e293b",
                mb: 1.5,
              }}
            >
              {question.title}
            </Typography>

            {question.question_type === 1 || question.question_type === 3 ? (
              question.question_type === 1 ? (
                // Dropdown-based Single Choice (e.g. Session Observations)
                <Box>
                  <TextField
                    select
                    fullWidth
                    size="small"
                    label={`Select ${question.title}`}
                    value={selectedChoiceId}
                    onChange={(e) => {
                      const newChoiceId = e.target.value;
                      const choiceObj = question.choices.find(
                        (c) => c.id === newChoiceId
                      );
                      const text = getAutoGeneratedText(
                        question.title,
                        choiceObj
                      );
                      onAnswerChange(sessionId, question.id, newChoiceId, text);
                    }}
                    SelectProps={{
                      MenuProps: {
                        PaperProps: {
                          style: { maxHeight: 320 },
                          sx: {
                            "& .MuiList-root": {
                              display: "flex !important",
                              flexDirection: "column !important",
                              padding: "6px 0 !important",
                            },
                            "& .MuiMenuItem-root": {
                              display: "flex !important",
                              flexDirection: "row !important",
                              alignItems: "center !important",
                              width: "100% !important",
                              whiteSpace: "normal !important",
                              minHeight: "40px !important",
                              py: 1,
                              px: 2,
                            },
                          },
                        },
                      },
                    }}
                    sx={{
                      backgroundColor: "#fff",
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "8px",
                      },
                    }}
                  >
                    <MenuItem value="" disabled style={{ display: "flex", width: "100%", padding: "10px 16px" }}>
                      <em>-- Select {question.title} --</em>
                    </MenuItem>
                    {question.choices.map((choice) => (
                      <MenuItem
                        key={choice.id}
                        value={choice.id}
                        style={{
                          display: "flex",
                          width: "100%",
                          padding: "10px 16px",
                          whiteSpace: "normal",
                        }}
                      >
                        {choice.title}
                      </MenuItem>
                    ))}
                  </TextField>

                  {/* Auto-generated text box */}
                  {selectedChoice && (
                    <Box
                      sx={{
                        mt: 1.5,
                        p: 2,
                        borderRadius: "8px",
                        background: "#f0fdf4",
                        border: "1px solid #bbf7d0",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "11px",
                          fontWeight: 700,
                          color: "#15803d",
                          letterSpacing: "0.5px",
                          textTransform: "uppercase",
                          mb: 0.5,
                        }}
                      >
                        Auto-generated text:
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: "14px",
                          color: "#166534",
                          lineHeight: 1.6,
                          fontWeight: 500,
                        }}
                      >
                        {autoText}
                      </Typography>
                    </Box>
                  )}
                </Box>
              ) : (
                // Multi-select choices
                (category.title.includes("WELL-BEING") ||
                  question.title.toLowerCase().includes("resources") ||
                  question.title.toLowerCase().includes("strategies") ||
                  (question.choices && question.choices.length > 18)) ? (
                  // DROPDOWN AND SELECT MULTIPLE (as requested for Section 7 Personalised Resources & Strategies)
                  <Box sx={{ my: 1 }}>
                    <TextField
                      select
                      fullWidth
                      size="small"
                      label={`Select ${question.title}`}
                      SelectProps={{
                        multiple: true,
                        MenuProps: {
                          PaperProps: {
                            style: { maxHeight: 320 },
                            sx: {
                              "& .MuiList-root": {
                                display: "flex !important",
                                flexDirection: "column !important",
                                padding: "6px 0 !important",
                              },
                              "& .MuiMenuItem-root": {
                                display: "flex !important",
                                flexDirection: "row !important",
                                alignItems: "center !important",
                                width: "100% !important",
                                whiteSpace: "normal !important",
                                minHeight: "40px !important",
                                py: 1,
                                px: 2,
                              },
                            },
                          },
                        },
                        renderValue: (selected) => (
                          <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                            {selected.map((val) => {
                              const ch = question.choices?.find((c) => c.id === val);
                              return (
                                <Chip
                                  key={val}
                                  label={ch?.title || val}
                                  size="small"
                                  sx={{
                                    backgroundColor: "#dbeafe",
                                    color: "#1e40af",
                                    fontWeight: 600,
                                    height: "24px",
                                  }}
                                />
                              );
                            })}
                          </Box>
                        ),
                      }}
                      value={existing?.choices || []}
                      onChange={(e) => {
                        const next =
                          typeof e.target.value === "string"
                            ? e.target.value.split(",")
                            : e.target.value;
                        onAnswerChange(sessionId, question.id, next, null);
                      }}
                      sx={{
                        backgroundColor: "#fff",
                        "& .MuiOutlinedInput-root": {
                          borderRadius: "8px",
                        },
                      }}
                    >
                      {question.choices?.map((choice) => {
                        const isChecked = (existing?.choices || []).includes(
                          choice.id
                        );
                        return (
                          <MenuItem
                            key={choice.id}
                            value={choice.id}
                            style={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              width: "100%",
                              padding: "8px 16px",
                              whiteSpace: "normal",
                            }}
                          >
                            <Checkbox
                              checked={isChecked}
                              size="small"
                              style={{ marginRight: "8px" }}
                            />
                            <Typography sx={{ fontSize: "14px", color: "#1e293b" }}>
                              {choice.title}
                            </Typography>
                          </MenuItem>
                        );
                      })}
                    </TextField>
                  </Box>
                ) : (
                  // Checkbox-based Multi-Select (Strengths / Areas as requested in Image 5)
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: {
                        xs: "1fr",
                        sm: "1fr 1fr",
                        md: "1fr 1fr 1fr",
                      },
                      gap: 1.25,
                      my: 1.5,
                    }}
                  >
                    {question.choices.map((choice) => {
                      const picked = existing?.choices || [];
                      const isChecked = picked.includes(choice.id);
                      return (
                        <Box
                          key={choice.id}
                          onClick={() => {
                            const next = isChecked
                              ? picked.filter((id) => id !== choice.id)
                              : [...picked, choice.id];
                            onAnswerChange(sessionId, question.id, next, null);
                          }}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            p: 1.2,
                            borderRadius: "8px",
                            border: isChecked
                              ? "1.5px solid #2563eb"
                              : "1px solid #e2e8f0",
                            backgroundColor: isChecked ? "#eff6ff" : "#fff",
                            cursor: "pointer",
                            transition: "all 0.15s ease",
                            "&:hover": {
                              backgroundColor: isChecked ? "#dbeafe" : "#f8fafc",
                              borderColor: isChecked ? "#1d4ed8" : "#cbd5e1",
                            },
                          }}
                        >
                          <Checkbox
                            checked={isChecked}
                            size="small"
                            sx={{
                              p: 0.5,
                              mr: 1,
                              color: isChecked ? "#2563eb" : "#94a3b8",
                              "&.Mui-checked": {
                                color: "#2563eb",
                              },
                            }}
                          />
                          <Typography
                            sx={{
                              fontSize: "14px",
                              fontWeight: isChecked ? 600 : 500,
                              color: isChecked ? "#1e40af" : "#334155",
                              userSelect: "none",
                            }}
                          >
                            {choice.title}
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                )
              )
            ) : question.question_type === 2 ? (
              // Voice & text input
              <div>
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    marginBottom: 8,
                  }}
                >
                  <span
                    style={{
                      fontWeight: 500,
                      marginRight: 8,
                      fontSize: "14px",
                    }}
                  >
                    Voice Input:
                  </span>
                  {isListening[question.id] ? (
                    <MicIcon
                      sx={{ color: "#16a34a", cursor: "pointer" }}
                      onClick={() => toggleListening(question.id)}
                      title="Click to stop voice input"
                    />
                  ) : (
                    <MicOffIcon
                      sx={{ color: "#dc2626", cursor: "pointer" }}
                      onClick={() => toggleListening(question.id)}
                      title="Click to start voice input"
                    />
                  )}
                  {isListening[question.id] && (
                    <span
                      style={{
                        marginLeft: 8,
                        color: "#16a34a",
                        fontSize: "12px",
                        fontWeight: 600,
                      }}
                    >
                      Listening...
                    </span>
                  )}
                </div>
                <TextField
                  size="small"
                  name="answer"
                  multiline
                  rows={3}
                  fullWidth
                  placeholder="Enter details..."
                  value={existing?.answer || ""}
                  onChange={(e) =>
                    onAnswerChange(sessionId, question.id, null, e.target.value)
                  }
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px",
                    },
                  }}
                />
              </div>
            ) : null}
          </Box>
        );
      })}

      <div style={{ textAlign: "right", marginTop: "14px", marginBottom: "8px" }}>
        <Button
          onClick={() => onSave(sessionId)}
          size="medium"
          variant="contained"
          style={{
            backgroundColor: "#2f327d",
            color: "#ffffff",
            padding: "9px 24px",
            borderRadius: "8px",
            fontWeight: 600,
            fontSize: "14px",
            textTransform: "none",
            boxShadow: "0 3px 10px rgba(47, 50, 125, 0.3)",
            cursor: "pointer",
            border: "none",
          }}
          sx={{
            mt: 1,
            mb: 2,
            backgroundColor: "#2f327d !important",
            color: "#ffffff !important",
            textTransform: "none",
            borderRadius: "8px",
            fontWeight: 600,
            fontSize: "14px",
            px: 3,
            py: 1.1,
            boxShadow: "0 3px 10px rgba(47, 50, 125, 0.3)",
            "&:hover": {
              backgroundColor: "#1e215a !important",
              boxShadow: "0 4px 14px rgba(47, 50, 125, 0.45) !important",
            },
          }}
        >
          ✓ {category.ai ? "Submit & Generate with AI" : `Submit ${category.title} Answer`}
        </Button>
      </div>
      {category.ai && (
        <Box
          sx={{
            border: "1px solid #b7e0dc",
            background: "#f2faf9",
            borderRadius: "8px",
            p: 2,
            mb: 2,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 600, color: "#1F7A78", marginBottom: 6 }}>
            AI-written text for the report
          </div>
          {aiText ? (
            <div style={{ whiteSpace: "pre-line", fontSize: 14, color: "#333" }}>
              {aiText}
            </div>
          ) : (
            <div style={{ fontSize: 13, color: "#999" }}>
              Nothing generated yet. Answer the questions above and submit.
            </div>
          )}
        </Box>
      )}
      {CustomComponent && <CustomComponent {...customComponentProps} />}
      <Divider sx={{ my: 3 }} />
    </div>
  );
};

export default DynamicCategorySection;